import React, { FC, useEffect, useMemo, useState } from "react";
import { Select as PdSelect } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import DropZone from "~/components/DropZone";
import Download from "~/components/Icons/default/Download";
import { NOTIFICATIONS } from "~/constans/notifications";
import { UploadResponseDto, useUploads } from "~/entity/Uploads";
import { useSetUploads } from "~/entity/Uploads/hooks/useSetUploads";
import { useSetLocationData } from "~/hooks/locationData";
import { uploadFile } from "~/libs/uploadFile";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import type { UploadDataFormProps } from "~/widgets/UploadDataModal";

import RegMenCheckStep from "./RegMenCheckStep";

import s from "./UploadRegMenForm.less";

type UploadStep = "upload" | "check";

const UploadRegMenForm: FC<UploadDataFormProps> = ({
  formId,
  onClose,
  refetch,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const setData = useSetLocationData();
  const [step, setStep] = useState<UploadStep>("upload");
  const [checkResult, setCheckResult] = useState<UploadResponseDto | null>(
    null,
  );
  const [file, setFile] = useState({});
  const [isDisabled, setIsDisabled] = useState(false);
  const { handleSubmit, control, reset } = useForm({
    defaultValues: {
      year: new Date().getFullYear(),
      quarter: 1,
    },
  });

  const { uploadDealerMetricFile } = useSetUploads();
  const { userToDealerTemplate } = useUploads();

  const [addNotificationMutation] = useAddNotificationMutation({});
  const fallBack = () => {
    addNotificationMutation({
      variables: {
        type: NOTIFICATIONS.error,
        content: t("common.data_uploaded_error"),
      },
    });
  };

  useEffect(() => {
    if (step === "check") {
      onStateChange({
        customFooter: true,
        width: 980,
        title: t("uploads.check_data"),
      });
    } else {
      onStateChange({
        loading: uploadDealerMetricFile.isLoading,
        submitDisabled: Object.keys(file).length === 0 || isDisabled,
        title: t("uploads.upload_data"),
      });
    }
  }, [
    step,
    uploadDealerMetricFile.isLoading,
    file,
    isDisabled,
    onStateChange,
    t,
  ]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const result: number[] = [];
    for (let i = currentYear + 3; i > currentYear - 13; i--) {
      result.push(i);
    }
    return result.map((y) => ({ label: String(y), value: y }));
  }, []);

  const quarterOptions = useMemo(
    () => [1, 2, 3, 4].map((q) => ({ label: String(q), value: q })),
    [],
  );

  const onSubmit = async (data) => {
    const { id: fileId } = (await uploadFile(file as File, fallBack)) || {};

    if (fileId) {
      uploadDealerMetricFile.mutate(
        {
          fileId: Number(fileId),
          year: data.year,
          quarter: data.quarter,
          type: "userToDealer",
        },
        {
          onSuccess: (res) => {
            // PASDILER-1964: бэк возвращает результат проверки файла.
            // Открываем экран «Проверка данных» с реальным ответом.
            const result = res?.data;
            if (result) {
              setCheckResult(result);
              setStep("check");
            } else {
              refetch?.();
              onClose();
            }
          },
          onError: () => {
            fallBack();
          },
        },
      );

      setData("page", 0);
      setIsDisabled(true);
    }
  };

  const handleBack = () => {
    setStep("upload");
    setCheckResult(null);
    setFile({});
    setIsDisabled(false);
    reset({
      year: new Date().getFullYear(),
      quarter: 1,
    });
  };

  // PASDILER-1964: accept идёт через стандартный flow DRAFT-записи
  // в списке загрузок (уведомление с confirm/cancel), как и для
  // остальных типов. Здесь — завершить просмотр и обновить список.
  const handleContinue = () => {
    refetch?.();
    onClose();
  };

  if (step === "check") {
    return (
      <RegMenCheckStep
        result={checkResult}
        onBack={handleBack}
        onContinue={handleContinue}
      />
    );
  }

  return (
    <form id={formId} className={s.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.field}>
        <span className={s.fieldLabel}>{t("uploads.year")}</span>
        <Controller
          name="year"
          control={control}
          render={({ onChange, value }) => (
            <PdSelect
              value={value}
              onChange={(v: number) => onChange(v)}
              options={yearOptions}
              style={{ width: "100%" }}
            />
          )}
        />
      </div>
      <div className={s.field}>
        <span className={s.fieldLabel}>{t("uploads.quarter")}</span>
        <Controller
          name="quarter"
          control={control}
          render={({ onChange, value }) => (
            <PdSelect
              value={value}
              onChange={(v: number) => onChange(v)}
              options={quarterOptions}
              style={{ width: "100%" }}
            />
          )}
        />
      </div>
      {/* PASDILER-1964: GET /uploads/template/user-to-dealer → text/csv (23 колонки) */}
      <a
        href="#"
        onClick={async (e) => {
          e.preventDefault();
          const blob = await userToDealerTemplate();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "regional-managers-template.csv";
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        }}
        className={s.templateBlock}
      >
        <Download fill="#4c5865" />
        <span className={s.templateText}>
          <span className={s.templateLink}>
            {t("uploads.upload_attachRegMen_template")}
          </span>
          <span className={s.templateHint}>
            {t("uploads.template_csv_hint")}
          </span>
        </span>
      </a>
      <DropZone
        getFiles={(files) => setFile(files[0])}
        accept={".csv"}
        isOne
        showFormats
      />
    </form>
  );
};

export default UploadRegMenForm;
